import { type UseMutationResult } from '@tanstack/react-query';

import { parseOrThrow } from '../../../lib/api/contract.js';
import { useApiMutation } from '../../../lib/api/hooks.js';
import { createPayload, type PayloadSpec } from '../../../lib/forms/payload.js';
import { tenantAdmins, tenantAdminEndpoints } from './endpoints.js';
import {
  createdTenantAdmin,
  type CreatedTenantAdmin,
  type TenantAdminFormInput,
} from './schemas.js';

/**
 * Writing a tenant administrator.
 *
 * The shape every mutation in the portal copies:
 *
 * - **An idempotency key per submission.** The Gateway retains keys for 24
 *   hours, so a double-submitted form is one administrator rather than two.
 * - **Invalidate the lists and the details** after a write. Over-invalidating
 *   costs a refetch; under-invalidating shows a reader a list without the
 *   thing they just created.
 * - **The response is parsed.** A create that returns an unexpected shape is
 *   the same contract failure as a read that does.
 * - **Errors are not swallowed.** `ApiError.fieldErrors` reaches the caller
 *   so the form can attach each message to the field that caused it.
 */
const WRITTEN_KEYS = tenantAdmins.writtenKeys;

/**
 * What the service requires, accepts and refuses.
 *
 * `email` and `password` are create-only: they cannot be changed through this
 * screen. `status` is update-only: every administrator is created ACTIVE.
 */
const TENANT_ADMIN_PAYLOAD: PayloadSpec = {
  required: ['tenantId', 'firstName', 'lastName'],
  optional: [],
  createOnly: ['email', 'password'],
  updateOnly: ['status'],
};

export function createTenantAdminPayload(values: TenantAdminFormInput): Record<string, unknown> {
  return createPayload(values, TENANT_ADMIN_PAYLOAD);
}

export function updateTenantAdminPayload(values: TenantAdminFormInput): Record<string, unknown> {
  return {
    tenantId: values.tenantId,
    firstName: values.firstName,
    lastName: values.lastName,
    status: values.status,
  };
}

export function useCreateTenantAdmin(): UseMutationResult<
  CreatedTenantAdmin,
  unknown,
  TenantAdminFormInput
> {
  return useApiMutation<TenantAdminFormInput, CreatedTenantAdmin>({
    mutationFn: async (api, input) => {
      const payload = await api.post<unknown>(
        tenantAdminEndpoints.create,
        createTenantAdminPayload(input),
        crypto.randomUUID(),
      );
      return parseOrThrow(createdTenantAdmin, payload, tenantAdminEndpoints.create);
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdateTenantAdminVariables {
  readonly userId: string;
  readonly values: TenantAdminFormInput;
}

export function useUpdateTenantAdmin(): UseMutationResult<
  unknown,
  unknown,
  UpdateTenantAdminVariables
> {
  return useApiMutation<UpdateTenantAdminVariables, unknown>({
    // PATCH, not PUT: the form owns only the name and status of an
    // administrator; permissions and other account fields belong elsewhere.
    mutationFn: async (api, { userId, values }) => {
      await api.patch<unknown>(
        tenantAdminEndpoints.update(userId),
        updateTenantAdminPayload(values),
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface DeleteTenantAdminVariables {
  readonly userId: string;
  readonly tenantId: string;
}

/**
 * Deleting a tenant administrator.
 *
 * Soft, always: the service sets `deleted_at` and `deleted_by` and leaves the
 * row where it is, because audit entries and historical actions still point at
 * it.
 */
export function useSoftDeleteTenantAdmin(): UseMutationResult<
  unknown,
  unknown,
  DeleteTenantAdminVariables
> {
  return useApiMutation<DeleteTenantAdminVariables, unknown>({
    mutationFn: async (api, { userId, tenantId }) => {
      await api.delete<unknown>(tenantAdminEndpoints.delete(userId, tenantId));
    },
    invalidate: WRITTEN_KEYS,
  });
}
